import React, { useState, useContext, useRef } from 'react';
import { QrReader } from 'react-qr-reader';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { useConfettiText } from '../../hooks/useConfettiText';
import useMobileView from '../../hooks/useMobileView';
import RewardCard from '../rewardCard/RewardCard';
import './qrScanCheckin.css';

const QRScanCheckin = ({ businessId, disabled = false }) => {
  const [result, setResult] = useState('');
  const [rewardData, setRewardData] = useState(null);
  const { player } = useContext(AuthContext);
  const mobileView = useMobileView();
  const qrCheckinRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [manualCode, setManualCode] = useState('');

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    qrCheckinRef,
    mobileView,
    '🎉 Business QR Check-in!',
    1200,
    'qrCheckin',
  );

  const playCheckinSound = () => {
    const audio = new Audio(
      'https://res.cloudinary.com/joegalaxy/video/upload/v1752624122/Futuristic_HUD_Sound_Design_Blake_Sanchez_touovl.mp3',
    );
    audio.currentTime = 0;
    audio.play();
    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
    }, 1000);
  };

  const handleScan = async (data) => {
    if (data) {
      try {
        const res = await axiosConn.post(
          'checkins',
          {
            businessId,
            qrCode: data,
            source: 'qr',
          },
          {
            headers: {
              Authorization: `Bearer ${player?.token}`,
            },
          },
        );
        setResult('✅ Check-in successful!');
        setRewardData(res.data.data.rewards);
        showConfettiText();
        playCheckinSound();
      } catch (err) {
        setResult(err.response?.data?.message || 'Error verifying QR code');
      }
    }
  };

  const handleError = (err) => {
    if (
      err &&
      err.name &&
      (err.name === 'NotAllowedError' ||
        err.name === 'NotFoundError' ||
        err.name === 'NotReadableError' ||
        err.name === 'OverconstrainedError')
    ) {
      setResult('Error accessing camera');
    } else {
      setResult('');
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualCode) return setResult('Please enter a QR code.');
    handleScan(manualCode);
  };

  if (disabled) {
    return (
      <div
        ref={qrCheckinRef}
        className="text-center"
        style={{
          fontSize: 28,
          margin: '18px 0',
          animation: 'pop 0.7s',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          color: '#22c55e',
        }}
      >
        <span role="img" aria-label="qr" style={{ fontSize: 38 }}>
          🎉
        </span>
        <span style={{ fontSize: 22, fontWeight: 700 }}>
          Already checked in!
        </span>
        <ConfettiTextComponent />
        <span
          className="badge"
          style={{
            background: '#e0e7eb',
            color: '#22c55e',
            fontWeight: 700,
            fontSize: 15,
            borderRadius: 10,
            marginTop: 8,
            boxShadow: '0 2px 8px #22c55e22',
            letterSpacing: 0.5,
            animation: 'pop 0.7s',
          }}
        >
          <span role="img" aria-label="check">
            ✔
          </span>{' '}
          Checked In!
        </span>
        <style>
          {`
            @keyframes pop {
              0% { transform: scale(0.7); opacity: 0; }
              60% { transform: scale(1.2); opacity: 1; }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }

  //   // Example reward data, replace with actual reward logic if available
  //   const rewardData = clue.reward || {
  //     xp: clue.xp || 25,
  //     jgk: clue.jgk || 5,
  //     badge: clue.badge || '',
  //     message: clue.rewardMessage || '',
  //     streak: clue.streak || undefined,
  //   };

  return (
    <div ref={qrCheckinRef} className="text-center">
      <div style={{ marginBottom: 12 }}>
        <button
          className="btn btn-sm btn-info mb-1"
          onClick={() => {
            setFacingMode(
              facingMode === 'environment' ? 'user' : 'environment',
            );
            setResult('');
          }}
        >
          Switch Camera
        </button>
      </div>
      <QrReader
        constraints={{ facingMode }}
        onResult={(result, error) => {
          if (!!result) {
            setResult('');
            handleScan(result?.text);
          }
          if (!!error) {
            handleError(error);
          }
        }}
        style={{ width: '100%', maxWidth: 340, margin: '0 auto' }}
      />
      <form onSubmit={handleManualSubmit} style={{ marginTop: 16 }}>
        <input
          type="text"
          className="form-control"
          placeholder="Enter QR code manually"
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          style={{ maxWidth: 260, margin: '0 auto', display: 'inline-block' }}
        />
        <button
          type="submit"
          className="btn btn-success ms-2"
          style={{
            fontWeight: 700,
            fontSize: 16,
            borderRadius: 10,
            boxShadow: '0 2px 8px #22c55e22',
            animation: 'pop 0.7s',
          }}
        >
          Check In
        </button>
      </form>
      {result && <p className="mt-2 small text-success">{result}</p>}
      {rewardData && (
        <div className="mt-4 mb-2">
          <RewardCard
            title="🎉 Reward Unlocked!"
            rewards={rewardData}
            showConfetti={true}
            category="checkin"
          />
        </div>
      )}
      <ConfettiTextComponent />
    </div>
  );
};

export default QRScanCheckin;
